import React, { useState, useEffect, useContext, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { AuthContext } from '../context/authContext';
import { Input, Label } from './ui';
import { claveContacto, coincideContacto, contactoEnLinea, textoContactoDesactualizado } from '../utils/contactos';

// -----------------------------------------------------------------------------
// Lista de contactos de proyecto, para cualquier scope.
//
//   scope = { tipo: 'ong', ongId }                          -> catalogo de la ONG
//   scope = { tipo: 'partido', tipoSubsidio, partido }      -> catalogo del
//                                                              tipo + partido
//   scope = null                                            -> sin catalogo
//                                                              (ONG nueva)
//
// Se usa en tres lugares con propositos distintos:
//
//  1. En el formulario de la OBRA: se elige de los contactos del scope, y si no
//     hay ninguno se puede dar de alta uno nuevo.
//  2. En el formulario de la ONG: se edita la lista completa.
//  3. En la pagina de contactos de proyecto: se administra la lista.
//
// En los tres, "Agregar contacto" y "Quitar" son LOCALES: solo cambian la
// lista. El unico que persiste es el guardado del padre (guardar la obra, la
// ONG, o "Guardar contactos" en la pagina), que manda la lista entera y
// reconcilia altas y bajas. Es a proposito: un alta inmediata dejaria filas
// huerfanas en el catalogo si despues no se guarda.
//
// La diferencia entre los ambitos es de donde sale el catalogo, no el
// componente. Los estilos van con los primitivos de ui/ para que los campos se
// vean igual que el resto de los formularios.
// -----------------------------------------------------------------------------

// URL para leer el catalogo del scope. Devuelve null cuando no hay catalogo
// (ONG todavia sin guardar): ahi los contactos viven solo en la lista local.
//
// El alta NO tiene URL: siempre es local. El unico que persiste es el guardado
// del padre (la obra, la ONG, o "Guardar contactos" en la pagina), que manda la
// lista entera y reconcilia altas y bajas. Se unifico asi para que "Agregar
// contacto" no deje filas huerfanas en el catalogo si despues no se guarda.
const urlsDeScope = (scope) => {
    if (!scope) return null;

    if (scope.tipo === 'ong' && scope.ongId) {
        return { listar: `/api/ongs/${scope.ongId}/contactos` };
    }

    if (scope.tipo === 'partido' && scope.tipoSubsidio && scope.partido) {
        const qs = new URLSearchParams({
            tipo_subsidio: scope.tipoSubsidio,
            partido: scope.partido
        }).toString();
        return { listar: `/api/contactos-proyecto?${qs}` };
    }

    return null;
};

const ListaContactosProyecto = forwardRef(function ListaContactosProyecto({
    scope,
    contactos,
    onChange,
    permitirAlta = true,
    permitirEdicion = true,
    etiquetaTitulo = 'Contactos del proyecto',
    mensajeVacio = 'No hay contactos cargados.',
    compacto = false,
    codigoObra
}, ref) {
    const { token } = useContext(AuthContext);
    const [catalogo, setCatalogo] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');
    const [filtro, setFiltro] = useState('');
    const [nuevo, setNuevo] = useState({ nombre: '', telefono: '', email: '' });
    const [editandoId, setEditandoId] = useState(null);
    const [edicion, setEdicion] = useState({ nombre: '', telefono: '', email: '' });

    // Se dependen las primitivas del scope y no el objeto: los padres lo arman
    // inline en cada render, asi que depender del objeto dispararia el fetch en
    // loop. Las URLs se derivan de esas primitivas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const urls = useMemo(() => urlsDeScope(scope), [
        scope?.tipo, scope?.ongId, scope?.tipoSubsidio, scope?.partido
    ]);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }), [token]);

    // Catalogo del scope. Se recarga cuando cambia el scope, y ademas el padre
    // puede pedir un refetch (recargarCatalogo) despues de guardar: si no, la
    // lista de "disponibles" queda vieja (no aparece lo recien creado y sigue
    // apareciendo lo dado de baja).
    const cargarCatalogo = useCallback(async () => {
        if (!urls) {
            setCatalogo([]);
            return;
        }
        setCargando(true);
        setError('');
        try {
            const r = await fetch(urls.listar, { headers });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            setCatalogo(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('[CONTACTOS] error al cargar catalogo:', e);
            setError('No se pudieron cargar los contactos del catálogo');
        } finally {
            setCargando(false);
        }
    }, [urls, headers]);

    useEffect(() => { cargarCatalogo(); }, [cargarCatalogo]);

    // Aviso de "esta obra muestra el dato viejo del catalogo" (migracion 069).
    //
    // Solo se consulta en la obra (codigoObra). En la ONG y en la pagina de
    // contactos el aviso es el modal previo al guardado, que es el lugar donde
    // tiene sentido: aca no hay nada que refrescar todavia.
    //
    // Si el GET falla se sigue sin aviso: es una ayuda, no un motivo para tapar
    // la lista de contactos.
    const [desactualizados, setDesactualizados] = useState({});

    useEffect(() => {
        if (!codigoObra) {
            setDesactualizados({});
            return;
        }
        let vivo = true;
        (async () => {
            try {
                const r = await fetch(
                    `/api/contactos-proyecto/pendientes?id_partido_obra=${encodeURIComponent(codigoObra)}`,
                    { headers }
                );
                if (!r.ok) return;
                const data = await r.json();
                if (!vivo || !Array.isArray(data)) return;
                setDesactualizados(Object.fromEntries(
                    data
                        .filter(p => p?.contacto_id > 0)
                        .map(p => [`id:${p.contacto_id}`, p])
                ));
            } catch (e) {
                console.error('[CONTACTOS] error al leer los pendientes:', e);
            }
        })();
        return () => { vivo = false; };
    }, [codigoObra, headers]);

    const elegidos = useMemo(
        () => (Array.isArray(contactos) ? contactos : []),
        [contactos]
    );

    // Lo que hay en el catalogo y todavia no esta elegido.
    const disponibles = useMemo(() => {
        const yaElegidos = new Set(elegidos.map(claveContacto));
        return catalogo.filter(c => !yaElegidos.has(claveContacto(c)));
    }, [catalogo, elegidos]);

    const filtrados = useMemo(
        () => disponibles.filter(c => coincideContacto(c, filtro)),
        [disponibles, filtro]
    );

    const elegir = (c) => onChange([...elegidos, c]);
    const quitar = (c) => {
        if (editandoId === claveContacto(c)) cancelarEdicion();
        onChange(elegidos.filter(x => claveContacto(x) !== claveContacto(c)));
    };

    const comenzarEdicion = (c) => {
        setEditandoId(claveContacto(c));
        setEdicion({ nombre: c.nombre, telefono: c.telefono, email: c.email });
    };

    const cancelarEdicion = () => {
        setEditandoId(null);
        setEdicion({ nombre: '', telefono: '', email: '' });
    };

    const guardarEdicion = () => {
        if (editandoId === null) return;
        const nuevoDato = {
            nombre: edicion.nombre.trim(),
            telefono: edicion.telefono.trim(),
            email: edicion.email.trim()
        };
        if (nuevoDato.nombre === '' && nuevoDato.telefono === '' && nuevoDato.email === '') return;
        onChange(elegidos.map(x =>
            claveContacto(x) === editandoId ? { ...x, ...nuevoDato } : x
        ));
        cancelarEdicion();
    };

    const limpiarNuevo = () => setNuevo({ nombre: '', telefono: '', email: '' });

    const contactoVacio = (nuevo.nombre === '' && nuevo.telefono === '' && nuevo.email === '');

    // Arma el contacto que esta tipeado y devuelve el objeto a agregar, sin
    // tocar la lista ni limpiar los inputs: de eso se encarga quien llama.
    //
    // contacto_id = 0 a proposito: todavia no existe en el catalogo. Lo crea el
    // guardado del padre (la obra, la ONG, o "Guardar contactos") en el mismo
    // momento en que manda la lista entera.
    const crearDesdePendiente = () => {
        if (contactoVacio) return null;
        return {
            contacto_id: 0,
            nombre: nuevo.nombre.trim(),
            telefono: nuevo.telefono.trim(),
            email: nuevo.email.trim()
        };
    };

    const agregarNuevo = () => {
        const item = crearDesdePendiente();
        if (!item) return;
        onChange([...elegidos, item]);
        limpiarNuevo();
    };

    // Para que el padre pueda: (a) agregar lo tipeado antes de guardar, asi el
    // boton "Guardar" no pierde el contacto, y (b) recargar el catalogo despues
    // de guardar.
    useImperativeHandle(ref, () => ({
        agregarPendiente: () => {
            const item = crearDesdePendiente();
            if (item) limpiarNuevo();
            return item;
        },
        recargarCatalogo: cargarCatalogo
    }));

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {etiquetaTitulo}
                </h4>
                {elegidos.length > 0 && (
                    <span className="text-xs text-gray-500">
                        {elegidos.length} seleccionado{elegidos.length > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Elegidos */}
            {elegidos.length > 0 ? (
                <ul className="space-y-1">
                    {elegidos.map((c) => {
                        const enEdicion = permitirEdicion && editandoId === claveContacto(c);
                        if (enEdicion) {
                            return (
                                <li
                                    key={`edit-${claveContacto(c)}`}
                                    className="px-3 py-2 rounded-md border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20"
                                >
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div>
                                            <Label htmlFor="contacto_editar_nombre">Nombre y apellido</Label>
                                            <Input
                                                id="contacto_editar_nombre"
                                                type="text"
                                                placeholder="Nombre y apellido"
                                                value={edicion.nombre}
                                                onChange={(e) => setEdicion({ ...edicion, nombre: e.target.value })}
                                                className="w-full text-sm"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="contacto_editar_telefono">Teléfono</Label>
                                            <Input
                                                id="contacto_editar_telefono"
                                                type="text"
                                                placeholder="Teléfono"
                                                value={edicion.telefono}
                                                onChange={(e) => setEdicion({ ...edicion, telefono: e.target.value })}
                                                className="w-full text-sm"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="contacto_editar_email">Email</Label>
                                            <Input
                                                id="contacto_editar_email"
                                                type="email"
                                                placeholder="Email"
                                                value={edicion.email}
                                                onChange={(e) => setEdicion({ ...edicion, email: e.target.value })}
                                                className="w-full text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={guardarEdicion}
                                            disabled={edicion.nombre.trim() === '' && edicion.telefono.trim() === '' && edicion.email.trim() === ''}
                                            className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 rounded-md"
                                        >
                                            Guardar cambios
                                        </button>
                                        <button
                                            type="button"
                                            onClick={cancelarEdicion}
                                            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </li>
                            );
                        }
                        return (
                            <li
                                key={claveContacto(c)}
                                className={`px-3 py-2 text-sm rounded-md border ${
                                    desactualizados[claveContacto(c)]
                                        ? 'border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20'
                                        : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="truncate text-gray-800 dark:text-gray-200">
                                        {contactoEnLinea(c)}
                                    </span>
                                    <span className="flex items-center gap-1 shrink-0">
                                        {permitirEdicion && (
                                            <button
                                                type="button"
                                                onClick={() => comenzarEdicion(c)}
                                                className="px-2 py-0.5 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-700 rounded"
                                                title="Editar los datos de este contacto"
                                            >
                                                Editar
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => quitar(c)}
                                            className="px-2 py-0.5 text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-300 dark:border-red-700 rounded"
                                            title="Quitar de la obra"
                                        >
                                            Quitar
                                        </button>
                                    </span>
                                </div>
                                {textoContactoDesactualizado(desactualizados[claveContacto(c)]) && (
                                    <p className="mt-1.5 text-xs text-amber-800 dark:text-amber-300">
                                        {textoContactoDesactualizado(desactualizados[claveContacto(c)])}
                                        {codigoObra && (
                                            <button
                                                type="button"
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    try {
                                                        const resp = await fetch(
                                                            `/api/contactos-proyecto/refrescar-obra?id_partido_obra=${encodeURIComponent(codigoObra)}`,
                                                            { method: 'POST', headers }
                                                        );
                                                        const json = await resp.json();
                                                        if (!resp.ok) return;
                                                        const cg12 = json.datos?.cg_12;
                                                        if (Array.isArray(cg12)) {
                                                            onChange(cg12);
                                                        }
                                                        setDesactualizados(prev => {
                                                            const n = { ...prev };
                                                            delete n[claveContacto(c)];
                                                            return n;
                                                        });
                                                    } catch (err) {
                                                        console.error('[CONTACTOS] no se pudo refrescar la obra:', err);
                                                    }
                                                }}
                                                className="ml-2 px-1.5 py-0.5 text-xs font-medium text-amber-900 bg-amber-200 hover:bg-amber-300 dark:text-amber-950 dark:bg-amber-300 dark:hover:bg-amber-400 rounded border border-amber-400 dark:border-amber-500"
                                            >
                                                Actualizar
                                            </button>
                                        )}
                                    </p>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="text-xs text-gray-500 italic">
                    {mensajeVacio}
                </p>
            )}

            {/* Disponibles del catalogo */}
            {disponibles.length > 0 && (
                <div className={compacto ? '' : 'pt-2 border-t border-gray-200 dark:border-gray-700'}>
                    <Input
                        type="text"
                        placeholder="Buscar en el catálogo..."
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        className="w-full text-sm mb-2"
                    />
                    <ul className="space-y-1 max-h-40 overflow-y-auto">
                        {filtrados.map((c) => (
                            <li key={claveContacto(c)}>
                                <button
                                    type="button"
                                    onClick={() => elegir(c)}
                                    className="w-full text-left px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                >
                                    + {contactoEnLinea(c)}
                                </button>
                            </li>
                        ))}
                        {filtrados.length === 0 && (
                            <li className="text-xs text-gray-500 italic px-1">
                                Ningún contacto coincide con «{filtro}».
                            </li>
                        )}
                    </ul>
                </div>
            )}

            {/* Alta de contacto nuevo */}
            {permitirAlta && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <Label htmlFor="contacto_nombre">Nombre y apellido</Label>
                            <Input
                                id="contacto_nombre"
                                type="text"
                                placeholder="Nombre y apellido"
                                value={nuevo.nombre}
                                onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <Label htmlFor="contacto_telefono">Teléfono</Label>
                            <Input
                                id="contacto_telefono"
                                type="text"
                                placeholder="Teléfono"
                                value={nuevo.telefono}
                                onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value })}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <Label htmlFor="contacto_email">Email</Label>
                            <Input
                                id="contacto_email"
                                type="email"
                                placeholder="Email"
                                value={nuevo.email}
                                onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={agregarNuevo}
                            disabled={contactoVacio}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-md"
                        >
                            Agregar contacto
                        </button>
                        {!contactoVacio && (
                            <button
                                type="button"
                                onClick={limpiarNuevo}
                                className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                Limpiar
                            </button>
                        )}
                    </div>
                </div>
            )}

            {cargando && <p className="text-xs text-gray-500">Cargando contactos...</p>}
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
    );
});

export default ListaContactosProyecto;
